// Numbers, costs and dates through Intl in the interface's language (Studio spec §7).
import { useTranslation } from "react-i18next";

export const formatNumber = (value: number, language: string): string =>
  new Intl.NumberFormat(language).format(value);

/** An amount in its currency (euros by default): two decimals, or four to six below one cent. */
export function formatCost(amount: number, language: string, currency = "EUR"): string {
  const small = amount !== 0 && Math.abs(amount) < 0.01;
  return new Intl.NumberFormat(language, {
    style: "currency",
    currency,
    minimumFractionDigits: small ? 4 : 2,
    maximumFractionDigits: small ? 6 : 2,
  }).format(amount);
}

/** A decimal the platform sends as text without a currency (knowledge costs): up to four decimals. */
export const formatDecimal = (text: string, language: string): string =>
  new Intl.NumberFormat(language, { maximumFractionDigits: 4 }).format(Number(text));

/** A duration: `850 ms`, `6.2 s`, `2 min 5 s`. */
export function formatDuration(ms: number, language: string): string {
  const unit = (value: number, name: "millisecond" | "second" | "minute", digits = 0) =>
    new Intl.NumberFormat(language, {
      style: "unit",
      unit: name,
      unitDisplay: "short",
      maximumFractionDigits: digits,
    }).format(value);
  if (ms < 1000) return unit(Math.round(ms), "millisecond");
  if (ms < 60_000) return unit(ms / 1000, "second", 1);
  const seconds = Math.round(ms / 1000); // Whole seconds first, so 119.6 s is 2 min 0 s.
  return `${unit(Math.floor(seconds / 60), "minute")} ${unit(seconds % 60, "second")}`;
}

export const formatDateTime = (date: Date | string, language: string): string =>
  new Intl.DateTimeFormat(language, { dateStyle: "medium", timeStyle: "short" }).format(
    new Date(date),
  );

/** A size in bytes, kilobytes or megabytes (1,024 each), such as "1.2 kB". */
export function formatBytes(bytes: number, language: string): string {
  const [unit, size] =
    bytes >= 1024 * 1024
      ? (["megabyte", 1024 * 1024] as const)
      : bytes >= 1024
        ? (["kilobyte", 1024] as const)
        : (["byte", 1] as const);
  return new Intl.NumberFormat(language, {
    style: "unit",
    unit,
    unitDisplay: "short",
    maximumFractionDigits: unit === "byte" ? 0 : 1,
  }).format(bytes / size);
}

const UNITS: Array<[Intl.RelativeTimeFormatUnit, number]> = [
  ["year", 365 * 24 * 3600],
  ["month", 30 * 24 * 3600],
  ["week", 7 * 24 * 3600],
  ["day", 24 * 3600],
  ["hour", 3600],
  ["minute", 60],
  ["second", 1],
];

/** How long ago (or in how long) a date is, such as "5 minutes ago", in the largest fitting unit. */
export function formatRelative(date: Date | string, language: string, now = Date.now()): string {
  const seconds = Math.round((new Date(date).getTime() - now) / 1000);
  const [unit, size] = UNITS.find(([, size]) => Math.abs(seconds) >= size) ?? ["second", 1];
  return new Intl.RelativeTimeFormat(language, { numeric: "auto" }).format(
    Math.round(seconds / size),
    unit,
  );
}

/** A date in UTC to the second, the same in every language: `2026-10-09 21:12:39 UTC`. */
export const formatUtc = (date: Date | string): string =>
  `${new Date(date).toISOString().slice(0, 19).replace("T", " ")} UTC`;

/** The formatters bound to the current language. */
export function useFormat() {
  const { i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  return {
    number: (value: number) => formatNumber(value, language),
    cost: (amount: number, currency?: string) => formatCost(amount, language, currency),
    decimal: (text: string) => formatDecimal(text, language),
    dateTime: (date: Date | string) => formatDateTime(date, language),
    relative: (date: Date | string) => formatRelative(date, language),
    bytes: (value: number) => formatBytes(value, language),
    duration: (ms: number) => formatDuration(ms, language),
    utc: formatUtc,
  };
}
